import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError, formatIndian } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  UploadCloud, Truck, CheckCircle2, AlertCircle, Package, ShieldCheck, Info,
  Image as ImageIcon, FileSpreadsheet, Loader2,
} from "lucide-react";

// CS 21.20 -- Dedicated Distributor Product Upload UI.
// * Route: /distributor-upload (master_admin only)
// * Independent of the Franchise Catalog upload (/upload). Targets
//   `distributor_stock` via POST /api/distributors/{did}/stock/upload.
// * Per-batch Sale / Memo status.
// * Requires an EXISTING distributor (no inline create).
// * Post-upload preview uses the SAME server-redacted projection as the
//   retailer link -- HO/admin sees {shape, pcs, total_wt, avg} on diamond
//   lines and a single customer_mrp + retailer_cost_total per piece. NEVER
//   a per-carat rate, bracket, or line-level cost.
export default function DistributorUpload() {
  const { user: me } = useAuth();
  const canUpload = me?.role === "master_admin";
  const [distributors, setDistributors] = useState([]);
  const [distId, setDistId] = useState("");
  const [status, setStatus] = useState("sale");
  const [shipmentLabel, setShipmentLabel] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [stock, setStock] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  // CS 21.25 -- Photos (mirrors the Wholesaler photos tab)
  const [xlsxFile, setXlsxFile] = useState(null);
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const [xlsxResult, setXlsxResult] = useState(null);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const inputRef = useRef();

  useEffect(() => {
    if (!canUpload) return;
    api.get("/distributors")
      .then((r) => setDistributors((r.data || []).filter((d) => d.is_active)))
      .catch(() => {});
  }, [canUpload]);

  const loadStock = useCallback(async (did) => {
    if (!did) { setStock(null); return; }
    setLoadingStock(true);
    try {
      const { data } = await api.get(`/distributors/${did}/stock`);
      setStock(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load stock");
      setStock(null);
    } finally {
      setLoadingStock(false);
    }
  }, []);

  useEffect(() => { if (distId) loadStock(distId); else setStock(null); }, [distId, loadStock]);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setResult(null);
  };

  // Reset photo state when distributor changes so we don't cross-post.
  useEffect(() => {
    setXlsxFile(null); setXlsxResult(null); setXlsxBusy(false);
    setBulkFiles([]); setBulkResult(null); setBulkBusy(false);
  }, [distId]);

  const uploadPhotoBook = async () => {
    if (!distId) return toast.error("Choose a distributor");
    if (!xlsxFile) return toast.error("Pick a .xlsx photo book");
    setXlsxBusy(true); setXlsxResult(null);
    try {
      const fd = new FormData();
      fd.append("file", xlsxFile);
      const { data } = await api.post(
        `/distributors/${distId}/photos/xlsx-embedded`, fd, { timeout: 600000 });
      setXlsxResult(data);
      toast.success(
        `Mapped ${data.mapped_count} · ${data.rows_without_image_count} without image · ${data.unmatched_count} unmatched`,
      );
      await loadStock(distId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setXlsxBusy(false); }
  };

  const uploadBulkPhotos = async () => {
    if (!distId) return toast.error("Choose a distributor");
    if (!bulkFiles.length) return toast.error("Pick one or more image files");
    setBulkBusy(true); setBulkResult(null);
    try {
      const fd = new FormData();
      bulkFiles.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/distributors/${distId}/photos/bulk`, fd);
      setBulkResult(data);
      toast.success(`Linked ${data.matched_count} · ${data.unmatched_count} unmatched`);
      await loadStock(distId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBulkBusy(false); }
  };

  const doUpload = async () => {
    if (!distId) return toast.error("Choose a distributor");
    if (!file) return toast.error("Choose an Excel file");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("status", status);
      if (shipmentLabel.trim()) fd.append("shipment_label", shipmentLabel.trim());
      // Let axios set the multipart boundary automatically -- do not set
      // Content-Type manually (it would drop the boundary).
      const { data } = await api.post(`/distributors/${distId}/stock/upload`, fd);
      setResult(data);
      toast.success(
        `${data.inserted} added · ${data.updated} updated (${data.status === "memo" ? "MEMO" : "SALE"})`
      );
      // Refresh redacted stock preview
      await loadStock(distId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const distName = distributors.find((d) => d.id === distId)?.name || "";

  if (!canUpload) {
    return (
      <div className="luxe-card p-8 text-center" data-testid="distributor-upload-forbidden">
        <ShieldCheck className="w-8 h-8 mx-auto text-slate-300 mb-2" />
        <p className="text-sm text-slate-500">Only Master Admins can upload Distributor Stock.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="distributor-upload-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2 flex items-center gap-2">
          <Truck className="w-3.5 h-3.5" /> Distributor Product Stock
        </p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Upload Distributor Stock</h1>
        <p className="text-slate-500 mt-2 max-w-2xl">
          Physical stock uploads target a specific distributor&apos;s private inventory.
          These are entirely separate from the Franchise Catalog and use shared
          Distribution Rate Settings. Diamond bracket rates and per-line costs
          are never surfaced on this page or the retailer link.
        </p>
      </div>

      <div className="luxe-card p-4 bg-[#FAF8F3] border-l-4 border-[#C9A84C] flex items-start gap-3">
        <Info className="w-4 h-4 text-[#C9A84C] mt-0.5 shrink-0" />
        <p className="text-xs text-slate-600">
          Distributor must exist before uploading stock. Create or activate the
          distributor on the <span className="font-medium text-[#0D1B2A]">Distributors</span> page first.
        </p>
      </div>

      <div className="luxe-card p-8 space-y-6" data-testid="distributor-upload-form">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Distributor</label>
            <Select value={distId} onValueChange={setDistId}>
              <SelectTrigger className="h-11 bg-white border-[#0D1B2A]/15" data-testid="distributor-select">
                <SelectValue placeholder="Select a distributor…" />
              </SelectTrigger>
              <SelectContent>
                {distributors.length === 0 && (
                  <div className="px-2 py-3 text-xs text-slate-400 italic">
                    No active distributors. Create one on the Distributors page.
                  </div>
                )}
                {distributors.map((d) => (
                  <SelectItem key={d.id} value={d.id} data-testid={`distributor-option-${d.id}`}>
                    {d.name}{d.city ? ` · ${d.city}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Batch Status</label>
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Batch status">
              <button
                type="button"
                onClick={() => setStatus("sale")}
                data-testid="status-sale-btn"
                className={`h-11 rounded-md border text-sm font-medium tracking-wide transition-colors ${
                  status === "sale"
                    ? "border-[#0D1B2A] bg-[#0D1B2A] text-white"
                    : "border-[#0D1B2A]/15 bg-white text-slate-600 hover:bg-[#FAF8F3]"
                }`}
              >
                Sale
              </button>
              <button
                type="button"
                onClick={() => setStatus("memo")}
                data-testid="status-memo-btn"
                className={`h-11 rounded-md border text-sm font-medium tracking-wide transition-colors ${
                  status === "memo"
                    ? "border-emerald-600 bg-emerald-600 text-white"
                    : "border-[#0D1B2A]/15 bg-white text-slate-600 hover:bg-[#FAF8F3]"
                }`}
              >
                Memo
              </button>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              Applies to every piece in this upload. Re-upload with the opposite
              status to flip pieces later.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">
              Shipment Label <span className="text-slate-300 normal-case">(optional)</span>
            </label>
            <input
              type="text"
              value={shipmentLabel}
              onChange={(e) => setShipmentLabel(e.target.value)}
              placeholder="e.g. Feb 2026 Consignment"
              className="w-full h-11 px-3 rounded-md border border-[#0D1B2A]/15 bg-white text-sm focus:outline-none focus:border-[#C9A84C]"
              data-testid="shipment-label-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Excel File</label>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
              onClick={() => inputRef.current?.click()}
              className="border-2 border-dashed border-[#0D1B2A]/15 rounded-md p-6 text-center cursor-pointer hover:border-[#C9A84C] transition-colors"
              data-testid="upload-dropzone"
            >
              <UploadCloud className="w-8 h-8 mx-auto text-[#C9A84C] mb-2" strokeWidth={1.5} />
              <p className="text-sm text-slate-600">{file ? file.name : "Click to choose or drag .xlsx here"}</p>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => handleFile(e.target.files[0])}
                data-testid="upload-file-input"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            onClick={doUpload}
            disabled={busy || !file || !distId}
            className="bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white disabled:opacity-50"
            data-testid="upload-submit-btn"
          >
            <UploadCloud className="w-4 h-4 mr-2" />
            {busy ? "Uploading…" : `Upload as ${status === "memo" ? "MEMO" : "SALE"}`}
          </Button>
        </div>
      </div>

      {result && (
        <div className="luxe-card p-6 border-l-4 border-emerald-500" data-testid="upload-result">
          <div className="flex items-center gap-3 text-emerald-700">
            <CheckCircle2 className="w-5 h-5" />
            <div className="text-sm">
              <span className="font-medium">{result.inserted}</span> added ·{" "}
              <span className="font-medium">{result.updated}</span> updated ·{" "}
              <span className="uppercase tracking-wider text-xs font-semibold">
                {result.status === "memo" ? "MEMO" : "SALE"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* CS 21.25 -- Photos section, mirrors the Wholesaler photos tab.
          Uses the same distributor picker above. Endpoints from CS 21.19/23. */}
      <div className="luxe-card p-8 space-y-6" data-testid="distributor-photos-section">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-[#C9A84C]" />
          <h2 className="font-serif text-2xl text-[#0D1B2A]">Photos</h2>
          {distName && (
            <span className="text-sm text-slate-500">
              — <span className="text-[#0D1B2A] font-medium">{distName}</span>
            </span>
          )}
          <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">
            Maps to ALL stock (Sale + Memo)
          </span>
        </div>

        {!distId && (
          <div className="text-xs text-slate-400 italic">
            Select a distributor above to enable photo uploads.
          </div>
        )}

        {/* Photo book (embedded-image .xlsx) — PRIMARY */}
        <div className="rounded-md border border-[#0D1B2A]/10 p-4 space-y-3" data-testid="photos-xlsx-block">
          <div className="flex items-center gap-2 text-sm">
            <FileSpreadsheet className="w-4 h-4 text-[#C9A84C]" />
            <span className="font-medium">Photo book (embedded-image .xlsx)</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              Primary
            </span>
          </div>
          <p className="text-xs text-slate-500">
            Column&nbsp;A = embedded image, col&nbsp;B = <span className="font-mono">JewelCode</span>,
            col&nbsp;C = <span className="font-mono">StyleCode</span>. One piece per row; images
            matched by row anchor. Match by JewelCode first, then StyleCode. De-duped by hash;
            resized for viewing.
          </p>
          <div className="flex flex-wrap gap-3 items-center">
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { setXlsxFile(e.target.files?.[0] || null); setXlsxResult(null); }}
              disabled={!distId || xlsxBusy}
              data-testid="dist-photos-xlsx-input"
              className="flex-1 min-w-[220px] text-sm file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#0D1B2A] file:text-white hover:file:bg-[#0D1B2A]/90"
            />
            <Button
              onClick={uploadPhotoBook}
              disabled={!distId || !xlsxFile || xlsxBusy}
              className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white disabled:opacity-50"
              data-testid="dist-photos-xlsx-upload-btn"
            >
              {xlsxBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing…</> : "Upload & map"}
            </Button>
          </div>
          {xlsxResult && (
            <div className="text-xs text-slate-600 space-y-1 pt-2 border-t border-slate-100" data-testid="dist-photos-xlsx-result">
              <div>
                <span className="font-medium">{xlsxResult.mapped_count}</span> mapped ·{" "}
                <span className="text-amber-700">{xlsxResult.rows_without_image_count}</span> rows without image ·{" "}
                <span className="text-amber-700">{xlsxResult.unmatched_count}</span> unmatched ·{" "}
                <span className="text-slate-500">{xlsxResult.dedupe_unique_blobs}</span> unique blobs
              </div>
              <div className="text-[11px] text-slate-500">
                Match breakdown:{" "}
                <span className="font-mono">{xlsxResult.matched_by_jewel_code_count}</span> by JewelCode ·{" "}
                <span className="font-mono">{xlsxResult.matched_by_style_code_count}</span> by StyleCode fallback
              </div>
              {(xlsxResult.unmatched || []).length > 0 && (
                <details className="text-[11px] text-amber-700">
                  <summary className="cursor-pointer">Show unmatched rows</summary>
                  <ul className="mt-2 max-h-40 overflow-y-auto space-y-0.5 font-mono">
                    {xlsxResult.unmatched.slice(0, 100).map((u, i) => (
                      <li key={`ux-${i}`}>
                        row {u.row_index ?? "?"} · JewelCode=<span className="text-slate-500">{u.jewel_code || "—"}</span> · StyleCode=<span className="text-slate-500">{u.style_code || "—"}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Bulk filename upload */}
        <div className="rounded-md border border-[#0D1B2A]/10 p-4 space-y-3" data-testid="photos-bulk-block">
          <div className="flex items-center gap-2 text-sm">
            <ImageIcon className="w-4 h-4 text-[#C9A84C]" />
            <span className="font-medium">Bulk photo upload</span>
          </div>
          <p className="text-xs text-slate-500">
            Drop image files whose filename matches a jewel code (e.g. <span className="font-mono">25P3886.jpg</span>) or
            style number. Only this distributor&apos;s stock is updated.
          </p>
          <input
            type="file" multiple accept="image/*"
            onChange={(e) => setBulkFiles(Array.from(e.target.files || []))}
            disabled={!distId || bulkBusy}
            data-testid="dist-photos-bulk-input"
            className="w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#0D1B2A] file:text-white hover:file:bg-[#0D1B2A]/90"
          />
          {bulkFiles.length > 0 && (
            <div className="text-xs text-slate-500">
              {bulkFiles.length} file{bulkFiles.length === 1 ? "" : "s"} selected ·{" "}
              <button onClick={() => setBulkFiles([])} className="text-[#0D1B2A] underline-offset-2 hover:underline">
                clear
              </button>
            </div>
          )}
          <div className="flex justify-end">
            <Button
              onClick={uploadBulkPhotos}
              disabled={!distId || !bulkFiles.length || bulkBusy}
              className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white disabled:opacity-50"
              data-testid="dist-photos-bulk-upload-btn"
            >
              {bulkBusy ? "Uploading…" : `Upload ${bulkFiles.length || ""} & auto-map`}
            </Button>
          </div>
          {bulkResult && (
            <div className="text-xs text-slate-600 space-y-1 pt-2 border-t border-slate-100" data-testid="dist-photos-bulk-result">
              <div>
                <span className="font-medium">{bulkResult.matched_count}</span> matched ·{" "}
                <span className="text-amber-700">{bulkResult.unmatched_count}</span> unmatched
              </div>
              {(bulkResult.unmatched || []).length > 0 && (
                <details className="text-[11px] text-amber-700">
                  <summary className="cursor-pointer">Show unmatched filenames</summary>
                  <ul className="mt-2 max-h-40 overflow-y-auto space-y-0.5 font-mono">
                    {bulkResult.unmatched.map((u, i) => (
                      <li key={`ub-${i}`}>{u.filename}{u.error ? ` — ${u.error}` : ""}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </div>

      {distId && (
        <div className="luxe-card p-6 space-y-4" data-testid="distributor-stock-preview">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-[#0D1B2A]" />
              <h2 className="font-serif text-xl text-[#0D1B2A]">
                Current stock — <span className="text-[#C9A84C]">{distName}</span>
              </h2>
              <span className="text-sm font-mono bg-[#0D1B2A]/5 text-[#0D1B2A] px-2 py-0.5 rounded" data-testid="stock-count">
                {stock?.count ?? 0}
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Redacted view · no per-line diamond cost
            </span>
          </div>

          {loadingStock ? (
            <div className="skeleton h-24" />
          ) : !stock || stock.count === 0 ? (
            <div className="text-center py-10 text-slate-400 border border-dashed border-[#0D1B2A]/10 rounded-md">
              <AlertCircle className="w-6 h-6 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No stock uploaded yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[#0D1B2A]/10">
                    <th className="py-2 pr-3">Jewel</th>
                    <th className="py-2 pr-3">Style</th>
                    <th className="py-2 pr-3">Purity</th>
                    <th className="py-2 pr-3 text-right">Net Wt</th>
                    <th className="py-2 pr-3 text-right">Dia Pcs</th>
                    <th className="py-2 pr-3 text-right">Dia Wt</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3 text-right">Retailer Cost</th>
                    <th className="py-2 pr-3 text-right">Customer MRP</th>
                  </tr>
                </thead>
                <tbody>
                  {stock.pieces.map((p) => (
                    <tr
                      key={p.id || p.jewel_code}
                      className="border-b border-[#0D1B2A]/5"
                      data-testid={`stock-row-${p.jewel_code}`}
                    >
                      <td className="py-2 pr-3 font-medium">{p.jewel_code}</td>
                      <td className="py-2 pr-3 text-slate-600">{p.style_no || "—"}</td>
                      <td className="py-2 pr-3">
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#0D1B2A]/10 text-slate-500">
                          {p.karat ? `${p.karat}KT` : "—"}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{formatIndian(p.net_wt_g, { decimals: 3 })} g</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{p.dia_pcs || 0}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{formatIndian(p.dia_wt_ct, { decimals: 3 })}</td>
                      <td className="py-2 pr-3">
                        <span
                          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                            p.status === "memo"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-100 text-slate-600"
                          }`}
                          data-testid={`stock-status-${p.jewel_code}`}
                        >
                          {p.status || "sale"}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums text-[#0D1B2A]">
                        ₹{formatIndian(p.retailer_cost_total, { decimals: 0 })}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums font-medium text-[#C9A84C]">
                        ₹{formatIndian(p.customer_mrp, { decimals: 0 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
